import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, MapPin, Clock, Users, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Formation {
  id: number;
  titre: string;
  description: string;
  prix: number;
  duree: number;
  format: 'presentiel' | 'en_ligne' | 'hybride';
  centre: {
    id: number;
    nom: string;
    logo?: string;
  };
  nombreVues: number;
  noteMoyenne: number;
  nombreAvis: number;
  visuel?: string;
  specialites: string[];
}

const FormationsPage: React.FC = () => {
  const { t } = useTranslation();
  const [formations, setFormations] = useState<Formation[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    format: '',
    specialites: '',
    prixMin: '',
    prixMax: '',
    dureeMin: '',
    dureeMax: '',
  });

  useEffect(() => {
    fetchFormations();
  }, [filters]);

  // Scroll to top when component mounts, but account for navbar
  useEffect(() => {
    // Small delay to ensure navbar is rendered
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  }, []);

  const fetchFormations = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await fetch(`/api/formations?${params}`);
      const data = await response.json();
      setFormations(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const formatFormation = (format: string) => {
    switch (format) {
      case 'presentiel': return t('home.formations.presentiel');
      case 'en_ligne': return t('home.formations.enLigne');
      case 'hybride': return t('home.formations.hybride');
      default: return format;
    }
  };

  const renderStars = (note: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={16}
        className={i < Math.floor(note) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
      />
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Spacer div to push content below navbar */}
      <div className="h-20"></div>
      
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('home.formations.title')}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.formations.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('home.formations.format')}
              </label>
              <select
                value={filters.format}
                onChange={(e) => setFilters({ ...filters, format: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('home.formations.allFormats')}</option>
                <option value="presentiel">{t('home.formations.presentiel')}</option>
                <option value="en_ligne">{t('home.formations.enLigne')}</option>
                <option value="hybride">{t('home.formations.hybride')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('home.formations.priceRange')} min
              </label>
              <input
                type="number"
                value={filters.prixMin}
                onChange={(e) => setFilters({ ...filters, prixMin: e.target.value })}
                placeholder="0"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('home.formations.priceRange')} max
              </label>
              <input
                type="number"
                value={filters.prixMax}
                onChange={(e) => setFilters({ ...filters, prixMax: e.target.value })}
                placeholder="10000"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('home.formations.duration')} min (h)
              </label>
              <input
                type="number"
                value={filters.dureeMin}
                onChange={(e) => setFilters({ ...filters, dureeMin: e.target.value })}
                placeholder="1"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('home.formations.duration')} max (h)
              </label>
              <input
                type="number"
                value={filters.dureeMax}
                onChange={(e) => setFilters({ ...filters, dureeMax: e.target.value })}
                placeholder="100"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={() => setFilters({
                  format: '',
                  specialites: '',
                  prixMin: '',
                  prixMax: '',
                  dureeMin: '',
                  dureeMax: '',
                })}
                className="w-full bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
              >
                {t('home.formations.clearFilters')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des formations */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {formations.map((formation) => (
            <motion.div
              key={formation.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
            >
              {formation.visuel && (
                <div className="h-48 bg-gray-200">
                  <img
                    src={formation.visuel}
                    alt={formation.titre}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {formatFormation(formation.format)}
                  </span>
                  <div className="flex items-center space-x-1">
                    {renderStars(formation.noteMoyenne)}
                    <span className="text-sm text-gray-600 ml-1">
                      ({formation.nombreAvis})
                    </span>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                  {formation.titre}
                </h3>

                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {formation.description}
                </p>

                <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                  <div className="flex items-center">
                    <Clock size={16} className="mr-1" />
                    {formation.duree}h
                  </div>
                  <div className="flex items-center">
                    <Users size={16} className="mr-1" />
                    {formation.nombreVues} {t('home.formations.views')}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {formation.centre.logo && (
                      <img
                        src={formation.centre.logo}
                        alt={formation.centre.nom}
                        className="w-6 h-6 rounded-full"
                      />
                    )}
                    <span className="text-sm text-gray-600">{formation.centre.nom}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-blue-600">
                      {formation.prix.toLocaleString()} DA
                    </div>
                    <div className="text-xs text-gray-500">{t('home.formations.perPerson', 'per person')}</div>
                  </div>
                </div>

                <button className="w-full mt-4 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors">
                  {t('home.formations.viewDetails')}
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {formations.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Search size={64} className="mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('home.formations.noResults')}
            </h3>
            <p className="text-gray-600">
              {t('home.formations.noResultsDesc')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormationsPage; 